<script setup>
import { onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import FormField from './FormField.vue';
import DateTimeField from './DateTimeField.vue';
import NamesList from './NamesList.vue';

const props = defineProps({
  modelValue: { type: Object, default: () => ({}) },
  errors: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();
const KEY = 'CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.FORM.FLIGHT';

const passengers = () => props.modelValue.passengers || {};

const set = patch =>
  emit('update:modelValue', { ...props.modelValue, ...patch });

const setNested = (key, patch) =>
  set({ [key]: { ...(props.modelValue[key] || {}), ...patch } });

const tripTypeOptions = [
  { value: 'one_way', label: t(`${KEY}.TRIP_TYPE_OPTIONS.ONE_WAY`) },
  { value: 'return', label: t(`${KEY}.TRIP_TYPE_OPTIONS.RETURN`) },
];

const cabinOptions = ['economy', 'premium_economy', 'business', 'first'].map(
  value => ({
    value,
    label: t(`${KEY}.CABIN_CLASS_OPTIONS.${value.toUpperCase()}`),
  })
);

const setTripType = tripType =>
  set(tripType === 'return' ? { tripType } : { tripType, returnAt: '' });

// The template falls back to "one_way" for display; persist that default so a
// one-way flight saved without touching the field still carries `tripType`.
onMounted(() => {
  if (!props.modelValue.tripType) set({ tripType: 'one_way' });
});
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="grid grid-cols-2 gap-2">
      <Input
        size="sm"
        :label="t(`${KEY}.AIRLINE`)"
        :placeholder="t(`${KEY}.AIRLINE_PLACEHOLDER`)"
        :model-value="modelValue.airline"
        @update:model-value="set({ airline: $event })"
      />
      <Input
        size="sm"
        :label="t(`${KEY}.FLIGHT_NUMBER`)"
        :placeholder="t(`${KEY}.FLIGHT_NUMBER_PLACEHOLDER`)"
        :model-value="modelValue.flightNumber"
        @update:model-value="set({ flightNumber: $event })"
      />
      <FormField :label="t(`${KEY}.TRIP_TYPE`)">
        <Select
          :model-value="modelValue.tripType || 'one_way'"
          :options="tripTypeOptions"
          class="!w-full"
          @update:model-value="setTripType"
        />
      </FormField>
      <FormField :label="t(`${KEY}.CABIN_CLASS`)">
        <Select
          :model-value="modelValue.cabinClass"
          :options="cabinOptions"
          :placeholder="
            t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.FORM.SELECT_PLACEHOLDER')
          "
          class="!w-full"
          @update:model-value="set({ cabinClass: $event })"
        />
      </FormField>
      <Input
        size="sm"
        :label="t(`${KEY}.ORIGIN`)"
        :placeholder="t(`${KEY}.AIRPORT_PLACEHOLDER`)"
        :model-value="(modelValue.origin || {}).locationCode"
        :message="errors['origin.locationCode']"
        :message-type="errors['origin.locationCode'] ? 'error' : 'info'"
        @update:model-value="setNested('origin', { locationCode: $event })"
      />
      <Input
        size="sm"
        :label="t(`${KEY}.DESTINATION`)"
        :placeholder="t(`${KEY}.AIRPORT_PLACEHOLDER`)"
        :model-value="(modelValue.destination || {}).locationCode"
        :message="errors['destination.locationCode']"
        :message-type="errors['destination.locationCode'] ? 'error' : 'info'"
        @update:model-value="setNested('destination', { locationCode: $event })"
      />
    </div>

    <DateTimeField
      :label="t(`${KEY}.DEPARTURE`)"
      :model-value="modelValue.departureAt"
      :error="errors.departureAt"
      @update:model-value="set({ departureAt: $event })"
    />
    <DateTimeField
      v-if="modelValue.tripType === 'return'"
      :label="t(`${KEY}.RETURN`)"
      :model-value="modelValue.returnAt"
      @update:model-value="set({ returnAt: $event })"
    />

    <div class="flex flex-col gap-2">
      <p class="text-xs font-semibold uppercase tracking-wide text-n-slate-10">
        {{ t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.PASSENGERS') }}
      </p>
      <div class="grid grid-cols-2 gap-2">
        <Input
          size="sm"
          type="number"
          min="0"
          :label="t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.ADULTS')"
          :model-value="passengers().adults"
          @update:model-value="setNested('passengers', { adults: $event })"
        />
        <Input
          size="sm"
          type="number"
          min="0"
          :label="t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.CHILDREN')"
          :model-value="passengers().children"
          @update:model-value="setNested('passengers', { children: $event })"
        />
      </div>
      <NamesList
        :label="t(`${KEY}.PASSENGER_NAMES`)"
        :model-value="passengers().names || []"
        @update:model-value="setNested('passengers', { names: $event })"
      />
    </div>

    <TextArea
      :model-value="modelValue.notes"
      :label="t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.FORM.NOTES')"
      :placeholder="
        t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.FORM.NOTES_PLACEHOLDER')
      "
      :max-length="2000"
      @update:model-value="set({ notes: $event })"
    />
  </div>
</template>
